import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Search, ShoppingCart, Heart, User, Menu, X, LogOut, Package } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { useWishlist } from '../context/WishlistContext';
import TrackOrderModal from './TrackOrderModal';
import axios from 'axios';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Header = () => {
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [banner, setBanner] = useState(null);
  const [trackOrderOpen, setTrackOrderOpen] = useState(false);
  const { user, isAuthenticated, isAdmin, logout } = useAuth();
  const { itemCount } = useCart();
  const { count: wishlistCount } = useWishlist();
  const navigate = useNavigate();
  const location = useLocation();

  const [navCategories, setNavCategories] = useState([]);

  useEffect(() => {
    axios.get(`${API}/banner`).then(r => setBanner(r.data)).catch(() => {});
    axios.get(`${API}/categories`).then(r => {
      setNavCategories(r.data.filter(c => c.visible_nav));
    }).catch(() => {});
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/shop?search=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery('');
      setSearchOpen(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const navLinks = [
    { name: 'All', href: '/shop' },
    { name: 'Trending', href: '/trending' },
    ...navCategories.map(c => ({ name: c.name, href: `/shop?category=${c.id}` })),
    { name: 'Random', href: '/random' },
  ];

  return (
    <header className="sticky top-0 z-50">
      {/* Dynamic Site Banner */}
      {banner?.enabled && banner?.text && (
        <div className="text-center py-1.5" style={{ backgroundColor: banner.bg_color || '#111', color: banner.text_color || '#fff' }} data-testid="shipping-banner">
          <p className="text-xs tracking-wider font-medium">
            {banner.text}
          </p>
        </div>
      )}
      <div className="bg-white shadow-md">
      <div className="max-w-[1800px] mx-auto px-4 md:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex-shrink-0" data-testid="logo-link">
            <img 
              src="https://customer-assets.emergentagent.com/job_print-store-6/artifacts/i6m0cwtm_Logo%20%281%29.png" 
              alt="PosterFire" 
              className="h-10 md:h-12 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
            {navLinks.map((link) => (
              <Link
                key={link.name}
                to={link.href}
                className="text-sm font-bold uppercase tracking-widest text-gray-700 hover:text-fire transition-colors"
                data-testid={`nav-${link.name.toLowerCase()}`}
              >
                {link.name}
              </Link>
            ))}
          </nav>

          {/* Right Actions */}
          <div className="flex items-center gap-2 md:gap-3">
            {/* Desktop Inline Search */}
            <form onSubmit={handleSearch} className="hidden md:flex items-center relative" data-testid="desktop-search-form">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search posters..."
                className="w-48 lg:w-64 bg-gray-100 border-0 py-2 pl-9 pr-3 text-sm text-gray-900 placeholder:text-gray-400 focus:ring-2 focus:ring-fire outline-none rounded-full"
                data-testid="desktop-search-input"
              />
              <Search className="w-4 h-4 text-gray-400 absolute left-3 pointer-events-none" />
            </form>

            {/* Mobile Search Toggle */}
            <button
              onClick={() => setSearchOpen(!searchOpen)}
              className="md:hidden p-2 text-gray-700 hover:text-fire transition-colors"
              data-testid="search-toggle"
            >
              <Search className="w-5 h-5" />
            </button>

            {/* Wishlist */}
            <Link 
              to="/wishlist" 
              className="p-2 text-gray-700 hover:text-fire transition-colors relative"
              data-testid="wishlist-link"
            >
              <Heart className="w-5 h-5" />
              {wishlistCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-fire text-white text-xs flex items-center justify-center rounded-full">
                  {wishlistCount}
                </span>
              )}
            </Link>

            {/* Cart */}
            <Link 
              to="/cart" 
              className="p-2 text-gray-700 hover:text-fire transition-colors relative"
              data-testid="cart-link"
            >
              <ShoppingCart className="w-5 h-5" />
              {itemCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-fire text-white text-xs flex items-center justify-center rounded-full">
                  {itemCount}
                </span>
              )}
            </Link>

            {/* User Menu - Admin only */}
            {isAuthenticated && isAdmin ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button 
                    className="p-2 text-gray-700 hover:text-fire transition-colors"
                    data-testid="user-menu-trigger"
                  >
                    <User className="w-5 h-5" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48 bg-white border-gray-200">
                  <div className="px-2 py-1.5">
                    <p className="text-sm font-medium text-gray-900">{user?.name}</p>
                    <p className="text-xs text-gray-500">{user?.email}</p>
                  </div>
                  <DropdownMenuSeparator className="bg-gray-200" />
                  <DropdownMenuItem asChild>
                    <Link to="/admin" className="cursor-pointer text-gray-700 hover:text-fire" data-testid="admin-link">
                      Admin Panel
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className="bg-gray-200" />
                  <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-fire" data-testid="logout-btn">
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : null}

            {/* Track My Order */}
            <button
              onClick={() => setTrackOrderOpen(true)}
              className="hidden md:flex items-center gap-2 px-4 py-2 bg-gray-900 text-white text-sm font-medium uppercase tracking-wider hover:bg-gray-700 transition-colors"
              data-testid="track-order-btn"
            >
              <Package className="w-4 h-4" />
              Track Order
            </button>

            {/* Mobile Menu Toggle */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-2 text-gray-700 hover:text-fire transition-colors"
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Mobile Search Bar */}
        {searchOpen && (
          <div className="md:hidden pb-4 animate-slide-in" data-testid="search-bar">
            <form onSubmit={handleSearch} className="flex">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search posters..."
                className="flex-1 bg-gray-100 border-0 py-4 px-6 text-gray-900 placeholder:text-gray-400 focus:ring-2 focus:ring-fire outline-none"
                autoFocus
                data-testid="search-input"
              />
              <button 
                type="submit" 
                className="bg-fire text-white px-8 py-4 font-bold uppercase tracking-wider hover:bg-fire-light transition-colors"
                data-testid="search-submit"
              >
                Search
              </button>
            </form>
          </div>
        )}

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden py-4 border-t border-gray-200 animate-fade-in" data-testid="mobile-menu">
            <nav className="flex flex-col gap-4">
              {navLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className="text-sm font-medium uppercase tracking-widest text-gray-700 hover:text-fire transition-colors py-2"
                >
                  {link.name}
                </Link>
              ))}
              {!isAuthenticated && (
                <button
                  onClick={() => { setMobileMenuOpen(false); setTrackOrderOpen(true); }}
                  className="bg-gray-900 text-white text-center py-3 font-bold uppercase tracking-wider mt-4 flex items-center justify-center gap-2"
                  data-testid="mobile-track-order"
                >
                  <Package className="w-4 h-4" />
                  Track My Order
                </button>
              )}
            </nav>
          </div>
        )}
      </div>
      </div>
      <TrackOrderModal open={trackOrderOpen} onClose={() => setTrackOrderOpen(false)} />
    </header>
  );
};

export default Header;
